import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Container,
    Row,
    Col,
    Card,
    CardHeader,
    CardBody,
    CardFooter,
    Table,
    Badge,
    Button,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem,
    Input,
    InputGroup,
    InputGroupAddon,
    InputGroupText,
    Pagination,
    PaginationItem,
    PaginationLink,
    Modal,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Form,
    FormGroup,
    Label,
    CustomInput,
} from "reactstrap";
import Select from "react-select";
import CommonHeader from "components/Headers/CommonHeader.js";
import Swal from "sweetalert2";
import {
    getAllRooms,
    createRoom as createRoomApi,
    updateRoomMetadata,
    archiveRoom,
    getAllCountries,
    getAllCities,
    getAllModerators,
    assignModerator,
    getAllRoomTypes
} from "../../api/ApiServices";
import { IMAGE_BASE_URL } from "../../api/ApiServices";

/* ═══════════════════════════════════════════════════
   INLINE STYLES – Premium Glassmorphism & Modern UI
   ═══════════════════════════════════════════════════ */
const styles = {
    statCard: {
        background: "linear-gradient(135deg, #ffffff 0%, #f8f9fe 100%)",
        borderRadius: "16px",
        border: "1px solid rgba(0,0,0,0.04)",
        boxShadow: "0 4px 15px rgba(0,0,0,0.03)",
        height: "100%",
        padding: "24px",
        position: "relative",
        overflow: "hidden"
    },
    filterBar: {
        background: "#f8f9fe",
        borderRadius: "12px",
        padding: "15px 20px",
        margin: "0 0 15px 0",
        border: "1px solid rgba(0,0,0,0.04)",
    },
    metricLabel: {
        fontSize: "11px",
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: "0.8px",
        color: "#8898aa",
    },
    tableRow: {
        transition: "all 0.15s ease",
        position: "relative",
    },
    roomAvatar: {
        width: "38px",
        height: "38px",
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: "15px",
        color: "#fff",
        flexShrink: 0,
        boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
    },
    pagination: {
        padding: "1rem 1.5rem",
        borderTop: "1px solid #e9ecef",
        background: "#f8f9fe",
        borderRadius: "0 0 16px 16px",
    },
    filterInputGroup: {
        borderRadius: "10px",
        border: "1px solid #e2e8f0",
        background: "#fff",
        overflow: "visible",
        width: "100%",
        display: "flex",
        alignItems: "center"
    },
    filterSelect: {
        border: "none",
        fontSize: "13px",
        fontWeight: 500,
        color: "#525f7f",
        paddingLeft: "5px",
        height: "38px"
    },
    filterIcon: {
        padding: "0 10px",
        fontSize: "14px",
    }
};

const modalSelectStyles = {
    control: (provided) => ({
        ...provided,
        borderRadius: "8px",
        minHeight: "45px",
        border: "1px solid #dee2e6",
        boxShadow: "none",
        "&:hover": { borderColor: "#5e72e4" },
        fontSize: "14px",
        background: "#fff",
        color: "#525f7f"
    }),
    option: (provided, state) => ({
        ...provided,
        backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
        color: state.isSelected ? "#fff" : "#525f7f",
        fontSize: "14px",
        padding: "10px 15px",
        cursor: "pointer"
    }),
    menu: (provided) => ({
        ...provided,
        borderRadius: "12px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        border: "none",
        marginTop: "8px",
        overflow: "hidden",
        zIndex: 9999
    }),
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    placeholder: (provided) => ({ ...provided, color: "#adb5bd" }),
    singleValue: (provided) => ({ ...provided, color: "#525f7f" })
};

const customSelectStyles = {
    control: (base) => ({
        ...base,
        border: "none",
        boxShadow: "none",
        background: "transparent",
        minHeight: "32px",
        cursor: "pointer"
    }),
    valueContainer: (base) => ({
        ...base,
        padding: "0 8px"
    }),
    input: (base) => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: "13px"
    }),
    singleValue: (base) => ({
        ...base,
        fontSize: "13px",
        fontWeight: 500,
        color: "#525f7f"
    }),
    placeholder: (base) => ({
        ...base,
        fontSize: "13px",
        fontWeight: 500,
        color: "#adb5bd"
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: "10px",
        marginTop: "8px",
        boxShadow: "0 10px 25px rgba(0,0,0,0.1)",
        overflow: "hidden",
        width: "max-content",
        minWidth: "160px"
    }),
    option: (base, state) => ({
        ...base,
        fontSize: "13px",
        fontWeight: 500,
        backgroundColor: state.isSelected ? "#5e72e4" : state.isFocused ? "#f8f9fe" : "#fff",
        color: state.isSelected ? "#fff" : "#525f7f",
        cursor: "pointer",
        "&:active": { backgroundColor: "#5e72e4", color: "#fff" }
    })
};

const ITEMS_PER_PAGE = 10;

const AVATAR_GRADIENTS = [
    "linear-gradient(135deg, #5e72e4, #825ee4)",
    "linear-gradient(135deg, #2dce89, #2dcecc)",
    "linear-gradient(135deg, #fb6340, #fbb140)",
    "linear-gradient(135deg, #11cdef, #1171ef)",
    "linear-gradient(135deg, #f5365c, #f56036)",
    "linear-gradient(135deg, #172b4d, #5e72e4)",
];

const formatNumber = (num) => {
    if (num >= 1000000) return (num / 1000000).toFixed(1) + "M";
    if (num >= 1000) return (num / 1000).toFixed(1) + "k";
    return num?.toString() || "0";
};

const FilterItem = ({ icon, children, color = "#adb5bd" }) => (
    <div style={styles.filterInputGroup}>
        <div style={{ ...styles.filterIcon, color }}>
            <i className={icon} />
        </div>
        <div style={{ flex: 1 }}>{children}</div>
    </div>
);

/* ═══════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════ */
const Rooms = () => {
    const renderPaginationItems = (currentPage, totalItems, limit, onPageChange) => {
        const totalPagesCount = Math.ceil(totalItems / limit);
        if (totalPagesCount <= 1) return null;

        const pages = [];
        const delta = 2;

        pages.push(
            <PaginationItem key={1} active={currentPage === 1}>
                <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(1); }}>1</PaginationLink>
            </PaginationItem>
        );

        if (currentPage > delta + 2) {
            pages.push(<PaginationItem key="start-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        const rangeStart = Math.max(2, currentPage - delta);
        const rangeEnd = Math.min(totalPagesCount - 1, currentPage + delta);

        for (let i = rangeStart; i <= rangeEnd; i++) {
            pages.push(
                <PaginationItem key={i} active={currentPage === i}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(i); }}>{i}</PaginationLink>
                </PaginationItem>
            );
        }

        if (currentPage < totalPagesCount - delta - 1) {
            pages.push(<PaginationItem key="end-ellipsis" disabled><PaginationLink href="#pablo" onClick={(e) => e.preventDefault()}>...</PaginationLink></PaginationItem>);
        }

        if (totalPagesCount > 1) {
            pages.push(
                <PaginationItem key={totalPagesCount} active={currentPage === totalPagesCount}>
                    <PaginationLink href="#pablo" onClick={(e) => { e.preventDefault(); onPageChange(totalPagesCount); }}>{totalPagesCount}</PaginationLink>
                </PaginationItem>
            );
        }

        return pages;
    };

    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const highlightId = queryParams.get("searchId") || queryParams.get("highlight");
    const urlSearchText = queryParams.get("searchText") || "";

    const [rooms, setRooms] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // Dynamic Lists for Modal & Filters
    const [allCountriesList, setAllCountriesList] = useState([]);
    const [isCountriesLoading, setIsCountriesLoading] = useState(false);
    const [activeCities, setActiveCities] = useState([]);
    const [modalCities, setModalCities] = useState([]);
    const [isCitiesLoading, setIsCitiesLoading] = useState(false);
    const [allAdmins, setAllAdmins] = useState([]);
    const [allRoomTypes, setAllRoomTypes] = useState([]);
    const [isRoomTypesLoading, setIsRoomTypesLoading] = useState(false);

    // Modals State
    const [createModalOpen, setCreateModalOpen] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [errors, setErrors] = useState({});
    const [roomFormData, setRoomFormData] = useState({
        id: null,
        name: "",
        type: "",
        country: "",
        city: "",
        visibility: "Public",
        eligible: true,
        tags: ""
    });

    const [moderatorModalOpen, setModeratorModalOpen] = useState(false);
    const [selectedRoomId, setSelectedRoomId] = useState(null);
    const [selectedModerator, setSelectedModerator] = useState("");

    // Filters
    const [searchTerm, setSearchTerm] = useState(urlSearchText);

    const [typeFilter, setTypeFilter] = useState("All");
    const [countryFilter, setCountryFilter] = useState("All");
    const [cityFilter, setCityFilter] = useState("All");
    const [visibilityFilter, setVisibilityFilter] = useState("All");
    const [eligibleFilter, setEligibleFilter] = useState("All");

    useEffect(() => {
        if (highlightId) {
            setTimeout(() => {
                const row = document.getElementById(`room-row-${highlightId}`);
                if (row) {
                    row.scrollIntoView({ behavior: "smooth", block: "center" });
                }
            }, 500);
        }
    }, [highlightId, loading]);

    // Fetch rooms from API
    const fetchRoomsData = useCallback(async () => {
        setLoading(true);
        try {
            const params = {
                page: currentPage,
                limit: ITEMS_PER_PAGE,
                search: searchTerm,
                type: typeFilter,
                country: countryFilter,
                city: cityFilter,
                visibility: visibilityFilter,
                eligible: eligibleFilter
            };
            const response = await getAllRooms(params);
            if ((response.status === true || response.status === "success") && response.payload) {
                setRooms(response.payload.rooms || []);
                setTotalCount(response.payload.total || 0);
            }
        } catch (error) {
            console.error("Error fetching rooms:", error);
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, typeFilter, countryFilter, cityFilter, visibilityFilter, eligibleFilter]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchRoomsData();
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [fetchRoomsData]);

    useEffect(() => {
        const fetchCountries = async () => {
            setIsCountriesLoading(true);
            try {
                const res = await getAllCountries(1, 1000, "", "1"); // Fetch active countries
                if (res.status === true || res.status === "success") {
                    const countries = res.payload?.countries || [];
                    console.log("Fetched Countries:", countries);
                    setAllCountriesList(countries);
                }
            } catch (err) {
                console.error("Error loading countries:", err);
            } finally {
                setIsCountriesLoading(false);
            }
        };
        fetchCountries();
    }, []);

    // Fetch Room Types for dropdowns
    useEffect(() => {
        const fetchRoomTypes = async () => {
            setIsRoomTypesLoading(true);
            try {
                const res = await getAllRoomTypes(1, 1000, "", "1"); // Fetch active types
                if (res.status === true || res.status === "success") {
                    setAllRoomTypes(res.payload?.roomTypes || []);
                }
            } catch (err) {
                console.error("Error loading room types:", err);
            } finally {
                setIsRoomTypesLoading(false);
            }
        };
        fetchRoomTypes();
    }, []);

    // Fetch cities for Filter
    useEffect(() => {
        const fetchFilterCities = async () => {
            if (countryFilter === "All") {
                setActiveCities([]);
                setCityFilter("All");
                return;
            }
            try {
                const res = await getAllCities(1, 1000, countryFilter, "");
                if (res.status === true || res.status === "success") {
                    setActiveCities(res.payload.cities || []);
                }
            } catch (err) {
                console.error("Error loading filter cities:", err);
            }
        };
        fetchFilterCities();
    }, [countryFilter]);

    // Fetch potential moderators for modal
    useEffect(() => {
        if (moderatorModalOpen) {
            getAllModerators().then(res => {
                if (res.status === true || res.status === "success") {
                    setAllAdmins(res.payload || []);
                }
            });
        }
    }, [moderatorModalOpen]);

    // Fetch cities for Modal
    useEffect(() => {
        const fetchModalCities = async () => {
            if (!roomFormData.country) {
                setModalCities([]);
                return;
            }
            setIsCitiesLoading(true);
            try {
                const res = await getAllCities(1, 1000, roomFormData.country, "");
                if (res.status === true || res.status === "success") {
                    setModalCities(res.payload.cities || []);
                }
            } catch (err) {
                console.error("Error loading modal cities:", err);
            } finally {
                setIsCitiesLoading(false);
            }
        };
        fetchModalCities();
    }, [roomFormData.country]);

    const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE);

    const handleAction = async (action, room, event) => {
        if (event) event.stopPropagation();

        if (action === "View Activity" || action === "Open Drawer") {
            navigate(`/admin/rooms-chats/${room.id}`);
            return;
        }

        if (action === "Edit Metadata") {
            setEditMode(true);
            setRoomFormData({
                id: room._id || room.id,
                name: room.r_name || room.name,
                type: room.r_type || room.type,
                country: room.r_country || "",
                city: room.r_city || "",
                visibility: room.r_visibility || "Public",
                eligible: room.r_eligible !== undefined ? room.r_eligible : true,
                tags: Array.isArray(room.r_tags) ? room.r_tags.join(", ") : ""
            });
            setCreateModalOpen(true);
            return;
        }

        if (action === "Assign Moderator") {
            const hasMod = (room.moderatorsInfo && room.moderatorsInfo.length > 0) || (room.r_moderators && room.r_moderators.length > 0);
            if (hasMod) {
                const modName = room.moderatorsInfo?.[0]?.au_name
                    ? `${room.moderatorsInfo[0].au_name} ${room.moderatorsInfo[0].au_surname || ""}`.trim()
                    : "A moderator";
                Swal.fire({
                    title: "Moderator Already Assigned",
                    html: `<b>${modName}</b> is already assigned to this room.<br/>Remove the current moderator first to assign a new one.`,
                    icon: "info",
                    confirmButtonColor: "#5e72e4",
                    confirmButtonText: "OK"
                });
                return;
            }
            setSelectedRoomId(room.id);
            setModeratorModalOpen(true);
            return;
        }

        const handleArchive = async (id, status) => {
            const result = await Swal.fire({
                title: "Are you sure?",
                text: `This will ${status === "Archived" ? "archive" : "restore"} the room.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: status === "Archived" ? "#f5365c" : "#2dce89",
                cancelButtonColor: "#8898aa",
                confirmButtonText: "Yes, proceed!"
            });

            if (result.isConfirmed) {
                try {
                    const res = await archiveRoom(id, status);
                    if (res.status === true || res.status === "success") {
                        Swal.fire("Success", `Room ${status === "Archived" ? "archived" : "restored"} successfully`, "success");
                        fetchRoomsData();
                    } else {
                        Swal.fire("Error", res.message || "Update failed", "error");
                    }
                } catch (err) {
                    console.error("Archive error:", err);
                    Swal.fire("Error", "Action failed", "error");
                }
            }
        };

        if (action === "Archive Room") {
            handleArchive(room.id, "Archived");
        } else if (action === "Restore Room") {
            handleArchive(room.id, "Active");
        }
    };
    const handleCreateEditSubmit = async (e) => {
        e.preventDefault();

        const newErrors = {};
        if (!roomFormData.name || !roomFormData.name.trim()) {
            newErrors.name = "Room Name is required";
        }
        if (!roomFormData.type) {
            newErrors.type = "Type is required";
        }
        if (!roomFormData.country) {
            newErrors.country = "Country is required";
        }
        if (!roomFormData.city) {
            newErrors.city = "City is required";
        }
        // if (!roomFormData.visibility) {
        //     newErrors.visibility = "Visibility is required";
        // }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        try {
            const payload = {
                // For update (schema format)
                r_name: roomFormData.name,
                r_type: roomFormData.type,
                r_tags: roomFormData.tags
                    ? roomFormData.tags.split(",").map(t => t.trim())
                    : [],
                r_country: roomFormData.country,
                r_city: roomFormData.city,
                r_visibility: roomFormData.visibility,
                r_eligible: roomFormData.eligible,

                // For create (backend params)
                name: roomFormData.name,
                type: roomFormData.type,
                tags: roomFormData.tags ? roomFormData.tags.split(",").map(t => t.trim()) : [],
                country: roomFormData.country,
                city: roomFormData.city,
                visibility: roomFormData.visibility,
                eligible: roomFormData.eligible
            };

            console.log("Sending payload:", payload);

            if (editMode) {
                const res = await updateRoomMetadata(roomFormData.id, payload);
                console.log("Update response:", res);

                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Saved!",
                        text: "Room metadata updated successfully.",
                        icon: "success",
                        showConfirmButton: false,
                        timer: 2000
                    });

                    setCreateModalOpen(false);
                    fetchRoomsData();

                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.message || "Update failed",
                        icon: "error",
                        showConfirmButton: false,
                        timer: 2000
                    });
                }

            } else {
                const res = await createRoomApi(payload);

                if (res.status === true || res.status === "success") {
                    Swal.fire({
                        title: "Created!",
                        text: "New room created successfully.",
                        icon: "success",
                        showConfirmButton: false,
                        timer: 2000
                    });

                    setCreateModalOpen(false);
                    fetchRoomsData();

                } else {
                    Swal.fire({
                        title: "Error",
                        text: res.message || "Creation failed",
                        icon: "error",
                        showConfirmButton: false,
                        timer: 2000
                    });
                }
            }

        } catch (err) {
            console.error("Submit error:", err);
            Swal.fire({
                title: "Error",
                text: err.response?.data?.message || err.message || "Action failed",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };
    const handleModeratorSubmit = async (e) => {
        e.preventDefault();

        if (!selectedModerator) return;

        try {
            const res = await assignModerator(selectedRoomId, { userId: selectedModerator });

            if (res.status === true || res.status === "success") {
                if (res.payload?.already_assigned) {
                    Swal.fire({
                        title: "Moderator Already Assigned",
                        text: res.message || "A moderator is already assigned to this room.",
                        icon: "info",
                        confirmButtonColor: "#5e72e4",
                        confirmButtonText: "OK"
                    });
                    setModeratorModalOpen(false);
                    setSelectedModerator("");
                    return;
                }

                Swal.fire({
                    title: "Success",
                    text: "Moderator assigned successfully",
                    icon: "success",
                    showConfirmButton: false,
                    timer: 2000
                });

                setModeratorModalOpen(false);
                setSelectedModerator("");
                fetchRoomsData();

            } else {
                Swal.fire({
                    title: "Error",
                    text: res.message || "Assignment failed",
                    icon: "error",
                    showConfirmButton: false,
                    timer: 2000
                });
            }

        } catch (err) {
            Swal.fire({
                title: "Error",
                text: "Assignment failed.",
                icon: "error",
                showConfirmButton: false,
                timer: 2000
            });
        }
    };
    const openCreateModal = () => {
        setEditMode(false);
        setErrors({});
        setRoomFormData({
            id: null,
            name: "",
            type: "",
            country: "",
            city: "",
            visibility: "Public",
            eligible: true,
            tags: ""
        });
        setCreateModalOpen(true);
    };

    useEffect(() => {
        const style = document.createElement('style');
        style.textContent = `
            .dropdown-menu { z-index: 9999 !important; position: absolute !important; will-change: transform; }
            .table-responsive { overflow-x: auto !important; }
            .custom-switch .custom-control-label::before { height: 1.5rem; width: 2.75rem; border-radius: 1rem; }
            .custom-switch .custom-control-label::after { width: calc(1.5rem - 4px); height: calc(1.5rem - 4px); border-radius: 1rem; }
            .custom-switch .custom-control-input:checked ~ .custom-control-label::after { transform: translateX(1.25rem); }
        `;
        document.head.appendChild(style);
        return () => { document.head.removeChild(style); };
    }, []);

    // Compute aggregated metrics from rooms already fetched
    const totalMembers = rooms.reduce((sum, r) => sum + (r.users || 0), 0);
    const totalMessages = rooms.reduce((sum, r) => sum + (r.messages || 0), 0);
    const totalActiveUsers = rooms.reduce((sum, r) => sum + (r.activeUsers || 0), 0);
    const allTopicsSet = new Set();
    rooms.forEach(r => (r.trendingTopics || []).forEach(t => allTopicsSet.add(t)));
    const totalTrendingTopics = allTopicsSet.size;

    return (
        <>
            <CommonHeader />
            <Container className="mt--7" fluid style={{
                position: "relative", zIndex: 1, overflowX: "hidden",
                overflowY: "auto"
            }}>

                {/* ── Stat Cards (Community-style) ── */}
                <Row className="mb-4">
                    <Col lg="3" md="6" className="mb-3 mb-lg-0">
                        <div style={styles.statCard}>
                            <h5 className="text-uppercase text-muted ls-1 mb-1">Total Rooms</h5>
                            <h3 className="mb-0 text-dark" style={{ fontSize: "28px" }}>
                                {loading ? <i className="fas fa-spinner fa-spin text-muted" /> : totalCount.toLocaleString()}
                            </h3>
                            <div className="text-primary text-sm font-weight-bold mt-2">
                                <i className="fas fa-door-open mr-1" /> Active Chat Rooms &amp; Groups
                            </div>
                        </div>
                    </Col>
                    <Col lg="3" md="6" className="mb-3 mb-lg-0">
                        <div style={styles.statCard}>
                            <h5 className="text-uppercase text-muted ls-1 mb-1">Total Members</h5>
                            <h3 className="mb-0 text-dark" style={{ fontSize: "28px" }}>
                                {loading ? <i className="fas fa-spinner fa-spin text-muted" /> : totalMembers.toLocaleString()}
                            </h3>
                            <div className="text-success text-sm font-weight-bold mt-2">
                                <i className="fas fa-users mr-1" /> Users Joined Across All Rooms
                            </div>
                        </div>
                    </Col>
                    <Col lg="3" md="6" className="mb-3 mb-lg-0">
                        <div style={styles.statCard}>
                            <h5 className="text-uppercase text-muted ls-1 mb-1">Message Volume</h5>
                            <h3 className="mb-0 text-dark" style={{ fontSize: "28px" }}>
                                {loading ? <i className="fas fa-spinner fa-spin text-muted" /> : totalMessages.toLocaleString()}
                            </h3>
                            <div className="text-info text-sm font-weight-bold mt-2">
                                <i className="fas fa-comments mr-1" /> Total Messages Across All Rooms
                            </div>
                        </div>
                    </Col>
                    <Col lg="3" md="6" className="mb-3 mb-lg-0">
                        <div style={styles.statCard}>
                            <h5 className="text-uppercase text-muted ls-1 mb-1">Active Users</h5>
                            <h3 className="mb-0 text-dark" style={{ fontSize: "28px" }}>
                                {loading ? <i className="fas fa-spinner fa-spin text-muted" /> : totalActiveUsers.toLocaleString()}
                            </h3>
                            <div className="text-warning text-sm font-weight-bold mt-2">
                                <i className="fas fa-fire mr-1" /> Unique Senders (Current Page)
                            </div>
                        </div>
                    </Col>
                </Row>

                <Row>
                    <Col>
                        <Card className="shadow" style={{ borderRadius: "16px", border: "none",  }}>
                            <CardHeader className="border-0 d-flex justify-content-between align-items-center flex-wrap" style={{ background: "#fff", padding: "20px 24px" }}>
                                <div>
                                    <h6 className="text-uppercase text-muted ls-1 mb-1" style={{ fontSize: "11px" }}>Management</h6>
                                    <h3 className="mb-0" style={{ fontSize: "20px", fontWeight: 700 }}>
                                        <i className="ni ni-chat-round text-green mr-2" /> Rooms & Chats
                                    </h3>
                                </div>
                                <Button size="sm" style={{ background: "linear-gradient(135deg, #5e72e4, #825ee4)", border: "none", color: "#fff" }} onClick={openCreateModal}>
                                    <i className="fas fa-plus mr-2" /> Create Room
                                </Button>
                            </CardHeader>

                            <CardBody style={styles.filterBar} className="border-top border-bottom">
                                <Row className="mb-3">
                                    <Col md="12">
                                        <InputGroup className="bg-white " style={{ borderRadius: "10px", border: "1px solid #e2e8f0" }}>
                                            <InputGroupAddon addonType="prepend">
                                                <InputGroupText className="bg-white border-0"><i className="fas fa-search text-muted" /></InputGroupText>
                                            </InputGroupAddon>
                                            <Input placeholder="Search by room name, country, city..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="border-0" />
                                        </InputGroup>
                                    </Col>
                                </Row>
                                <Row className="align-items-center ">
                                    <Col md="2" className="mt-2 mb-1">
                                        <FilterItem icon="fas fa-globe-africa" color="#5e72e4">
                                            <Select
                                                styles={customSelectStyles}
                                                placeholder="Country"
                                                value={countryFilter === "All" ? { value: "All", label: "All Countries" } : { value: countryFilter, label: allCountriesList.find(c => c.c_uuid === countryFilter)?.c_name }}
                                                onChange={(opt) => setCountryFilter(opt.value)}
                                                options={[{ value: "All", label: "All Countries" }, ...allCountriesList.map(c => ({ value: c.c_uuid, label: c.c_name }))]}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                        </FilterItem>
                                    </Col>
                                    <Col md="2" className="mt-2 mb-1">
                                        <FilterItem icon="fas fa-city" color="#2dce89">
                                            <Select
                                                styles={customSelectStyles}
                                                placeholder="City"
                                                value={cityFilter === "All" ? { value: "All", label: "All Cities" } : { value: cityFilter, label: activeCities.find(ci => ci.ci_uuid === cityFilter)?.ci_name }}
                                                onChange={(opt) => setCityFilter(opt.value)}
                                                options={[{ value: "All", label: "All Cities" }, ...activeCities.map(ci => ({ value: ci.ci_uuid, label: ci.ci_name }))]}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                        </FilterItem>
                                    </Col>
                                    <Col md="2" className="mt-2 mb-1">
                                        <FilterItem icon="fas fa-layer-group" color="#fb6340">
                                            <Select
                                                styles={customSelectStyles}
                                                placeholder="Type"
                                                value={typeFilter === "All" ? { value: "All", label: "All Types" } : { value: typeFilter, label: allRoomTypes.find(rt => rt.rt_uuid === typeFilter)?.rt_name || typeFilter }}
                                                onChange={(opt) => setTypeFilter(opt.value)}
                                                options={[{ value: "All", label: "All Types" }, ...allRoomTypes.map(rt => ({ value: rt.rt_uuid, label: rt.rt_name }))]}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                        </FilterItem>
                                    </Col>
                                    <Col md="2" className="mt-2 mb-1">
                                        <FilterItem icon="fas fa-eye" color="#11cdef">
                                            <Select
                                                styles={customSelectStyles}
                                                placeholder="Visibility"
                                                value={visibilityFilter === "All" ? { value: "All", label: "Visibility" } : { value: visibilityFilter, label: visibilityFilter }}
                                                onChange={(opt) => setVisibilityFilter(opt.value)}
                                                options={[
                                                    { value: "All", label: "Visibility" },
                                                    { value: "Public", label: "Public" },
                                                    { value: "Private", label: "Private" }
                                                ]}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                        </FilterItem>
                                    </Col>
                                    <Col md="2" className="mt-2 mb-1">
                                        <FilterItem icon="fas fa-star" color="#fbb140">
                                            <Select
                                                styles={customSelectStyles}
                                                placeholder="Rep Eligibility"
                                                value={eligibleFilter === "All" ? { value: "All", label: "Rep Eligibility" } : { value: eligibleFilter, label: eligibleFilter === "true" ? "Eligible" : "Ineligible" }}
                                                onChange={(opt) => setEligibleFilter(opt.value)}
                                                options={[
                                                    { value: "All", label: "Rep Eligibility" },
                                                    { value: "true", label: "Eligible" },
                                                    { value: "false", label: "Ineligible" }
                                                ]}
                                                isSearchable={true}
                                                menuPortalTarget={document.body}
                                            />
                                        </FilterItem>
                                    </Col>
                                    <Col md="2" className="text-right">
                                        <span style={{ fontSize: "12px", color: "#8898aa", fontWeight: 600 }}>{totalCount} rooms found</span>
                                    </Col>
                                </Row>
                            </CardBody>

                            <Table className="align-items-center table-flush" responsive >
                                <thead className="thead-light">
                                    <tr>
                                        <th>Room Name</th>
                                        <th>Type</th>
                                        <th>Country</th>
                                        <th>Total Users</th>
                                        <th>Messages</th>
                                        <th>Eligible</th>
                                        <th>Status</th>
                                        <th className="text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {loading ? (
                                        <tr><td colSpan="8" className="text-center py-5"><i className="fas fa-spinner fa-spin fa-2x text-primary" /></td></tr>
                                    ) : (
                                        rooms.map((room, idx) => (
                                            <tr
                                                key={room.id}
                                                id={`room-row-${room.id}`}
                                                style={{
                                                    ...styles.tableRow,
                                                    backgroundColor: highlightId === String(room.id) ? "rgba(45, 206, 137, 0.1)" : "transparent",
                                                    transition: "background-color 0.5s ease"
                                                }}
                                            >
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div
                                                            style={{
                                                                ...styles.roomAvatar,
                                                                background: AVATAR_GRADIENTS[idx % AVATAR_GRADIENTS.length],
                                                                overflow: "hidden"
                                                            }}
                                                            className="mr-3"
                                                        >
                                                            {(() => {

                                                                console.log("Room Data =>", room);

                                                                const rawImage = room?.r_image || room?.image;

                                                                const roomName =
                                                                    room?.r_name ||
                                                                    room?.name ||
                                                                    "Room";

                                                                let imageUrl = null;

                                                                if (rawImage) {

                                                                    // If full URL already exists
                                                                    if (rawImage.startsWith("http")) {

                                                                        imageUrl = rawImage;

                                                                    } else {

                                                                        // Remove extra slashes
                                                                        const cleanImage = rawImage.replace(/^\/+/, "");

                                                                        // CORRECT S3 PATH
                                                                        imageUrl =
                                                                            `https://cradleaibucket.s3.us-east-1.amazonaws.com/communities/${cleanImage.split("/").pop()}`;
                                                                    }
                                                                }

                                                                console.log("FINAL IMAGE URL =>", imageUrl);

                                                                return imageUrl ? (
                                                                    <img
                                                                        src={imageUrl}
                                                                        alt={roomName}
                                                                        loading="lazy"
                                                                        referrerPolicy="no-referrer"
                                                                        onError={(e) => {
                                                                            console.log("Image Load Error =>", imageUrl);
                                                                            e.currentTarget.onerror = null;
                                                                            e.currentTarget.src = "https://via.placeholder.com/100x100.png?text=Room";
                                                                        }}
                                                                        style={{
                                                                            width: "100%",
                                                                            height: "100%",
                                                                            objectFit: "cover",
                                                                            borderRadius: "50%"
                                                                        }}
                                                                    />
                                                                ) : (
                                                                    <span>{roomName?.charAt(0)?.toUpperCase() || "R"}</span>
                                                                );
                                                            })()}
                                                        </div>
                                                        <div className="d-flex flex-column">
                                                             <span
                                                                 style={{
                                                                     fontWeight: 600,
                                                                     fontSize: "14px"
                                                                 }}
                                                             >
                                                                 {room?.r_name || room?.name}
                                                             </span>
                                                             {room.trendingTopics && room.trendingTopics.length > 0 && (
                                                                 <div className="d-flex flex-wrap mt-1" style={{ gap: "4px" }}>
                                                                     {room.trendingTopics.map((topic, tIdx) => (
                                                                         <span key={tIdx} className="badge" style={{ 
                                                                             fontSize: "9px", 
                                                                             padding: "2px 6px", 
                                                                             background: "rgba(94, 114, 228, 0.08)",
                                                                             color: "#5e72e4",
                                                                             borderRadius: "8px",
                                                                             border: "1px solid rgba(94, 114, 228, 0.15)",
                                                                             fontWeight: "600"
                                                                         }}>
                                                                             #{topic}
                                                                         </span>
                                                                     ))}
                                                                 </div>
                                                             )}
                                                         </div>
                                                    </div>
                                                </td>
                                                <td>
                                                    <Badge color="" style={{ background: "#f1eaff", color: "#825ee4", border: "none", fontSize: "10px" }} className="text-uppercase">
                                                        {room.type}
                                                    </Badge>
                                                </td>
                                                <td><span style={{ fontSize: "13px", fontWeight: 500 }}>{room.country}</span></td>
                                                <td>
                                                     <div className="d-flex flex-column">
                                                         {/* <span style={{ fontSize: "13px", fontWeight: 600, color: "#5e72e4" }}>{formatNumber(room.activeUsers || 0)}</span> */}
                                                         <span style={{ fontSize: "13px", fontWeight: 600, color: "#5e72e4" }}>{formatNumber(room.users)}</span>
                                                     </div>
                                                </td>
                                                <td><span style={{ fontSize: "13px", fontWeight: 600 }}>{formatNumber(room.messages)}</span></td>
                                                <td>
                                                    <Badge color={room.eligible ? "success" : "warning"} pill style={{ fontSize: "10px" }} className="text-uppercase">
                                                        {room.eligible ? "✓ Eligible" : "✗ Ineligible"}
                                                    </Badge>
                                                </td>
                                                <td>
                                                    <div className="d-flex align-items-center">
                                                        <div style={{ width: "8px", height: "8px", borderRadius: "50%", background: room.status === "Active" ? "#2dce89" : "#adb5bd", marginRight: "8px" }} />
                                                        <span style={{ fontSize: "13px", color: room.status === "Active" ? "#2dce89" : "#8898aa" }}>{room.status}</span>
                                                    </div>
                                                </td>
                                                <td className="text-right">
                                                    <div className="d-flex justify-content-end align-items-center" style={{ gap: "8px" }}>
                                                        <Button size="sm" color="primary" style={{ background: "#5e72e4", border: "none", borderRadius: "50%", width: "32px", height: "32px", padding: 0 }} onClick={() => handleAction("View Activity", room)}>
                                                            <i className="fas fa-eye" />
                                                        </Button>
                                                        <UncontrolledDropdown>
                                                            <DropdownToggle size="sm" className="btn-icon-only" style={{ background: "transparent", border: "none" }}>
                                                                <i className="fas fa-ellipsis-v" style={{ color: "#6c757d" }} />
                                                            </DropdownToggle>
                                                            <DropdownMenu right className="shadow-lg border-0" style={{ borderRadius: "12px" }}>
                                                                <DropdownItem onClick={() => handleAction("Edit Metadata", room)}><i className="fas fa-edit text-info mr-2" /> Edit Details</DropdownItem>
                                                                <DropdownItem onClick={() => handleAction("Assign Moderator", room)}><i className="fas fa-user-shield text-success mr-2" /> Assign Moderator</DropdownItem>
                                                                <DropdownItem divider />
                                                                <DropdownItem onClick={() => handleAction(room.status === "Archived" ? "Restore Room" : "Archive Room", room)} className={room.status === "Archived" ? "text-success" : "text-danger"}>
                                                                    <i className={`fas fa-${room.status === "Archived" ? "undo" : "archive"} mr-2`} />
                                                                    {room.status === "Archived" ? "Restore Room" : "Archive Room"}
                                                                </DropdownItem>
                                                            </DropdownMenu>
                                                        </UncontrolledDropdown>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </Table>

                            <CardFooter className="py-4 border-0 d-flex justify-content-between align-items-center" style={styles.pagination}>
                                <small className="text-muted">
                                    Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, totalCount)} of {totalCount} rooms
                                </small>
                                <nav aria-label="...">
                                    <Pagination className="pagination justify-content-end mb-0" listClassName="justify-content-end mb-0">
                                        <PaginationItem disabled={currentPage === 1}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => { e.preventDefault(); setCurrentPage(currentPage - 1); }}
                                                tabIndex="-1"
                                            >
                                                <i className="fas fa-angle-left" />
                                            </PaginationLink>
                                        </PaginationItem>

                                        {renderPaginationItems(currentPage, totalCount, ITEMS_PER_PAGE, setCurrentPage)}

                                        <PaginationItem disabled={currentPage === totalPages}>
                                            <PaginationLink
                                                href="#pablo"
                                                onClick={(e) => { e.preventDefault(); setCurrentPage(currentPage + 1); }}
                                            >
                                                <i className="fas fa-angle-right" />
                                            </PaginationLink>
                                        </PaginationItem>
                                    </Pagination>
                                </nav>
                            </CardFooter>
                        </Card>
                    </Col>
                </Row>
            </Container>

            {/* Create/Edit Modal */}
            <Modal isOpen={createModalOpen} toggle={() => setCreateModalOpen(!createModalOpen)} centered size="lg">
                <ModalHeader toggle={() => setCreateModalOpen(false)}>
                    <span style={{ fontSize: "25px", fontWeight: "bold" }}>{editMode ? "Edit Room" : "Create New Room"}</span>
                </ModalHeader>
                <Form onSubmit={handleCreateEditSubmit}>
                    <ModalBody className="p-4">
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label>Room Name <span className="text-danger">*</span></Label>
                                    <Input
                                        type="text"
                                        style={{ fontSize: "16px", height: "auto", padding: "12px 15px", color: "#172b4d", fontWeight: "500" }}
                                        value={roomFormData.name}
                                        invalid={!!errors.name}
                                        onChange={e => {
                                            setRoomFormData({ ...roomFormData, name: e.target.value });
                                            if (errors.name) setErrors({ ...errors, name: null });
                                        }}
                                    />
                                    {errors.name && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.name}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label>Type <span className="text-danger">*</span></Label>
                                    <Select
                                        styles={modalSelectStyles}
                                        options={allRoomTypes.map(rt => ({ value: rt.rt_uuid, label: rt.rt_name }))}
                                        value={(() => {
                                            const match = allRoomTypes.find(rt => rt.rt_uuid === roomFormData.type || rt.rt_name === roomFormData.type);
                                            return match ? { value: match.rt_uuid, label: match.rt_name } : roomFormData.type ? { value: roomFormData.type, label: roomFormData.type } : null;
                                        })()}
                                        onChange={(opt) => {
                                            setRoomFormData({ ...roomFormData, type: opt ? opt.value : "" });
                                            if (errors.type) setErrors({ ...errors, type: null });
                                        }}
                                        placeholder="Select Room Type"
                                        isLoading={isRoomTypesLoading}
                                        isSearchable={true}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.type && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.type}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row>
                            <Col md="6">
                                <FormGroup>
                                    <Label>Country <span className="text-danger">*</span></Label>
                                    <Select
                                        value={allCountriesList.find(c => c.c_uuid === roomFormData.country) ? { value: roomFormData.country, label: allCountriesList.find(c => c.c_uuid === roomFormData.country).c_name } : null}
                                        onChange={opt => {
                                            setRoomFormData({ ...roomFormData, country: opt ? opt.value : "", city: "" });
                                            if (errors.country) setErrors({ ...errors, country: null });
                                        }}
                                        options={allCountriesList.map(c => ({ value: c.c_uuid, label: c.c_name }))}
                                        placeholder="Search Country..."
                                        isSearchable={true}
                                        isLoading={isCountriesLoading}
                                        isClearable={true}
                                        styles={modalSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.country && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.country}</div>}
                                </FormGroup>
                            </Col>
                            <Col md="6">
                                <FormGroup>
                                    <Label>City <span className="text-danger">*</span></Label>
                                    <Select
                                        value={modalCities.find(ci => ci.ci_uuid === roomFormData.city) ? { value: roomFormData.city, label: modalCities.find(ci => ci.ci_uuid === roomFormData.city).ci_name } : null}
                                        onChange={opt => {
                                            setRoomFormData({ ...roomFormData, city: opt ? opt.value : "" });
                                            if (errors.city) setErrors({ ...errors, city: null });
                                        }}
                                        options={modalCities.map(ci => ({ value: ci.ci_uuid, label: ci.ci_name }))}
                                        placeholder="Search City..."
                                        isSearchable={true}
                                        isLoading={isCitiesLoading}
                                        isClearable={true}
                                        styles={modalSelectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                    />
                                    {errors.city && <div className="text-danger mt-1" style={{ fontSize: "0.8rem" }}>{errors.city}</div>}
                                </FormGroup>
                            </Col>
                        </Row>
                        <Row className="align-items-center">
                            {/* <Col md="6">
                                <FormGroup>
                                    <Label>Visibility <span className="text-danger">*</span></Label>
                                    <Input type="select" value={roomFormData.visibility} onChange={e => setRoomFormData({ ...roomFormData, visibility: e.target.value })}>
                                        <option value="Public">Public</option>
                                        <option value="Private">Private</option>
                                    </Input>
                                </FormGroup>
                            </Col> */}
                            <Col md="6" className="d-none d-md-block"></Col>
                            <Col md="6" className="mt-2 text-center">
                                <Label className="d-block mb-1">Reputation Eligible</Label>
                                <CustomInput type="switch" id="eligibleSwitch" label="" checked={roomFormData.eligible} onChange={e => setRoomFormData({ ...roomFormData, eligible: e.target.checked })} />
                            </Col>
                        </Row>
                    </ModalBody>
                    <ModalFooter><Button color="secondary" onClick={() => setCreateModalOpen(false)}>Cancel</Button><Button color="primary" type="submit">{editMode ? "Save Changes" : "Create Room"}</Button></ModalFooter>
                </Form>
            </Modal>

            {/* Moderator Modal */}
            <Modal isOpen={moderatorModalOpen} toggle={() => setModeratorModalOpen(!moderatorModalOpen)} centered>
                <ModalHeader toggle={() => setModeratorModalOpen(false)}>Assign Moderator</ModalHeader>
                <Form onSubmit={handleModeratorSubmit}>
                    <ModalBody>
                        <FormGroup>
                            <Label>Select Moderator</Label>
                            <Select
                                value={allAdmins.find(a => String(a._id) === selectedModerator) ? { value: selectedModerator, label: `${allAdmins.find(a => String(a._id) === selectedModerator).au_name || "N/A"} ${allAdmins.find(a => String(a._id) === selectedModerator).au_surname || ""} (${allAdmins.find(a => String(a._id) === selectedModerator).au_email || ""})` } : null}
                                onChange={(opt) => setSelectedModerator(opt ? opt.value : "")}
                                options={allAdmins.map(a => ({ value: String(a._id), label: `${a.au_name || "N/A"} ${a.au_surname || ""} (${a.au_email || ""})` }))}
                                placeholder="Search by name or email..."
                                isSearchable={true}
                                styles={modalSelectStyles}
                                menuPortalTarget={document.body}
                                menuPosition="fixed"
                                isClearable={true}
                            />
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="secondary" onClick={() => setModeratorModalOpen(false)}>Cancel</Button>
                        <Button color="success" type="submit" disabled={!selectedModerator}>Assign</Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </>
    );
};

export default Rooms;